import {
  Component,
  input,
  ViewContainerRef,
  ViewChild,
  OnInit,
  OnDestroy,
  inject,
  PLATFORM_ID,
  ChangeDetectionStrategy,
  ComponentRef,
  Type,
  signal,
  effect,
  ErrorHandler,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import type { ComponenteModyo } from '@core/interfaces/pagina-modyo.interface';
import type { ModyoEntry } from '@core/interfaces/modyo';
import { componentRegistry } from '@shared/registry';
import { ServicioAdaptadorModyo } from '@core/services/modyo';

@Component({
  selector: 'app-renderizador-dinamico',
  standalone: true,
  imports: [],
  templateUrl: './renderizador-dinamico.component.html',
  styleUrl: './renderizador-dinamico.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RenderizadorDinamicoComponent implements OnInit, OnDestroy {
  @ViewChild('contenedorDinamico', { read: ViewContainerRef, static: true })
  protected referenciaContenedor!: ViewContainerRef;

  public readonly configuracionComponente = input.required<ComponenteModyo | ModyoEntry<unknown>>();

  public readonly registroComponentes = input<Map<string, Type<unknown>>>();

  public readonly usarRegistryGlobal = input<boolean>();

  protected readonly platformId = inject(PLATFORM_ID);
  protected readonly esNavegador = isPlatformBrowser(this.platformId);
  protected readonly adaptador = inject(ServicioAdaptadorModyo);

  protected readonly tieneError = signal<boolean>(false);

  protected readonly usarPlaceholder = signal<boolean>(false);

  protected readonly configuracion = signal<ComponenteModyo | ModyoEntry<unknown> | null>(null);

  protected referenciaComponente?: ComponentRef<unknown>;

  private readonly errorHandler = inject(ErrorHandler);

  constructor() {
    this.configurarReaccionCambioConfiguracion();
  }

  public ngOnInit(): void {
    this.inicializarConfiguracionComponente();
  }

  public ngOnDestroy(): void {
    this.destruirComponente();
  }

  protected renderizarComponente(): void {
    const config = this.configuracion();

    if (!config) {
      return;
    }

    if (!this.referenciaContenedor) {
      return;
    }

    let componente: ComponenteModyo | undefined;

    try {
      componente = this.normalizarConfiguracion(config);

      const tipoComponente = this.resolverTipoComponente(componente.tipoComponente);

      if (!tipoComponente) {
        this.usarPlaceholder.set(true);
        this.tieneError.set(false);
        return;
      }

      this.usarPlaceholder.set(false);
      this.destruirComponente();

      this.referenciaComponente = this.referenciaContenedor.createComponent(tipoComponente);

      this.aplicarPropiedadesComponente(this.referenciaComponente, componente.propiedades);

      this.referenciaComponente.changeDetectorRef.detectChanges();
      this.tieneError.set(false);
    } catch (error) {
      this.errorHandler.handleError(error);
      this.tieneError.set(true);
      this.usarPlaceholder.set(true);
      this.destruirComponente();
      return;
    }
  }

  protected normalizarConfiguracion(config: ComponenteModyo | ModyoEntry<unknown>): ComponenteModyo {
    if (this.esComponenteModyo(config)) {
      return config;
    }

    const convertido = this.adaptador.entryAComponenteModyo(config);
    return convertido;
  }

  protected esComponenteModyo(config: ComponenteModyo | ModyoEntry<unknown>): config is ComponenteModyo {
    return 'tipoComponente' in config && 'propiedades' in config;
  }

  protected resolverTipoComponente(tipoComponente: string): Type<unknown> | undefined {
    const TAMANIO_VACIO = 0;
    const usarGlobal = this.usarRegistryGlobal() ?? true;

    const componenteExisteEnRegistroGlobal = usarGlobal && componentRegistry.has(tipoComponente);
    if (componenteExisteEnRegistroGlobal) {
      return componentRegistry.get(tipoComponente);
    }

    const registroLocal = this.registroComponentes() ?? new Map<string, Type<unknown>>();
    const tieneComponentesEnRegistroLocal = registroLocal.size > TAMANIO_VACIO;

    if (tieneComponentesEnRegistroLocal) {
      const componente = registroLocal.get(tipoComponente);
      if (componente) {
        return componente;
      }
    }

    return undefined;
  }

  protected aplicarPropiedadesComponente(
    referenciaComponente: ComponentRef<unknown>,
    propiedades: Record<string, unknown>,
  ): void {
    if (!propiedades) {
      return;
    }

    const instancia = referenciaComponente.instance as Record<string, unknown>;

    Object.entries(propiedades).forEach(([clave, valor]) => {
      if (clave in instancia) {
        instancia[clave] = valor;
      }
    });
  }

  protected destruirComponente(): void {
    if (this.referenciaComponente) {
      this.referenciaComponente.destroy();
      this.referenciaComponente = undefined;
    }

    if (this.referenciaContenedor) {
      this.referenciaContenedor.clear();
    }
  }

  private configurarReaccionCambioConfiguracion(): void {
    effect(() => {
      const config = this.configuracion();
      if (config) {
        this.renderizarComponente();
      }
    });
  }

  private inicializarConfiguracionComponente(): void {
    const configuracionInicial = this.configuracionComponente();

    if (!configuracionInicial) {
      return;
    }

    this.configuracion.set(configuracionInicial);
  }
}
